import { useEffect, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { getAiEngine, type AiEngine } from "@/lib/ai-engine";
import { platformKey, type StudioItem } from "./helpers";

// ── Workspace ───────────────────────────────────────────────────────────────

export function useActiveWorkspace() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const q = useQuery<any[]>({ queryKey: ["/api/workspaces"] });
  const workspace = useMemo(() => (q.data || []).find((w: any) => w.id === activeWorkspaceId), [q.data, activeWorkspaceId]);
  const engine: AiEngine = getAiEngine(workspace);
  return { workspaceId: activeWorkspaceId, workspace, engine, isLoading: q.isLoading };
}

/**
 * The signed-in user's role in the active workspace (GET /api/workspaces/:id/my-role, same key the Settings page uses).
 * Only an owner or admin can publish to WordPress live; everyone else gets a preview from the server.
 * `canPublishLive` is null when the role could not be read; the buttons stay usable then because the server enforces it either way.
 */
export function useWorkspaceRole() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const q = useQuery<{ role: string } | null>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "my-role"],
    enabled: !!activeWorkspaceId,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/my-role`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
  });
  const role = q.data?.role ?? null;
  const canPublishLive: boolean | null = role === null ? null : role === "owner" || role === "admin";
  return { role, canPublishLive, isLoading: q.isLoading };
}

/** wordpressAppPassword is stripped server-side, so URL + username is the "configured" signal (same as the Blogs tab). */
export function wordpressConfigured(workspace: any): boolean {
  return !!(workspace?.wordpressUrl && workspace?.wordpressUsername);
}

/** Mirrors the Library tab's connection check so both screens agree. */
export function platformConnected(workspace: any, platform: string): boolean {
  const integ = (workspace?.integrations as any) || {};
  const key = platform.toLowerCase();
  if (key === "instagram" || key === "meta" || key === "facebook") {
    return !!(integ.meta?.connected || integ.instagram?.connected || integ.facebook?.connected || integ.instagram_business?.connected);
  }
  if (key === "x" || key === "twitter") return !!integ.x?.connected;
  if (key === "linkedin") return !!integ.linkedin?.connected;
  if (key === "google") return !!integ.google?.connected;
  return false;
}

export function itemPlatformConnected(workspace: any, item: StudioItem): boolean {
  return platformConnected(workspace, platformKey(item));
}

// ── "New since your last visit" ─────────────────────────────────────────────

const lastVisitKey = (workspaceId: number) => `drpr-studio-last-visit-${workspaceId}`;

/** localStorage is a per-viewer convenience only: it can be empty, blocked or throw, and the screen must work without it. */
function readLastVisit(workspaceId: number): number | null {
  try {
    const n = Number(localStorage.getItem(lastVisitKey(workspaceId)));
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function writeLastVisit(workspaceId: number): void {
  try {
    localStorage.setItem(lastVisitKey(workspaceId), String(Date.now()));
  } catch {
    /* blocked or full: the divider just won't show next time */
  }
}

/**
 * When the viewer last left Content Studio on this workspace (epoch ms), or null on a first visit.
 * The value is read once per workspace per mount, so the "New since your last visit" divider stays put while the
 * viewer clicks around; the new timestamp is saved when they leave the studio or close the tab.
 */
export function useLastVisit(workspaceId: number | null | undefined): number | null {
  const previous = useMemo(() => (workspaceId ? readLastVisit(workspaceId) : null), [workspaceId]);
  useEffect(() => {
    if (!workspaceId) return;
    const save = () => writeLastVisit(workspaceId);
    window.addEventListener("pagehide", save);
    return () => {
      window.removeEventListener("pagehide", save);
      save();
    };
  }, [workspaceId]);
  return previous;
}

// ── Single item (Review view) ───────────────────────────────────────────────

function unwrap(json: any): StudioItem | null {
  if (!json || typeof json !== "object") return null;
  const candidate = json.data && typeof json.data === "object" && "id" in json.data ? json.data : json.item && typeof json.item === "object" ? json.item : json;
  return candidate && typeof candidate === "object" && "id" in candidate ? (candidate as StudioItem) : null;
}

/**
 * Full row for the Review view. Contract: GET /api/workspaces/:id/content/:contentId.
 * Falls back to GET /api/content/:id, and finally to the list row the caller already has.
 */
export function useContentItem(id: number | null, listItem: StudioItem | undefined) {
  const { activeWorkspaceId } = useWorkspaceStore();
  const q = useQuery<StudioItem | null>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "content", "item", id],
    enabled: !!activeWorkspaceId && id !== null,
    staleTime: 15_000,
    queryFn: async () => {
      const urls = [`/api/workspaces/${activeWorkspaceId}/content/${id}`, `/api/content/${id}`];
      for (const url of urls) {
        try {
          const res = await fetch(url, { credentials: "include" });
          if (res.ok) {
            const item = unwrap(await res.json());
            if (item) return item;
          }
        } catch {
          /* try the next URL */
        }
      }
      return null;
    },
  });
  const item = useMemo<StudioItem | undefined>(() => {
    if (q.data && listItem) return { ...listItem, ...q.data };
    return q.data ?? listItem;
  }, [q.data, listItem]);
  return { item, isLoading: q.isLoading && !listItem, detailLoaded: !!q.data };
}

// ── Actions ─────────────────────────────────────────────────────────────────

/** apiRequest throws "<status>: <body>"; pull a human sentence out of it. */
export function errorMessage(err: unknown, fallback: string): string {
  const raw = String((err as any)?.message || "");
  const body = raw.replace(/^\d+:\s*/, "");
  try {
    const parsed = JSON.parse(body);
    if (parsed?.message) return String(parsed.message);
    if (parsed?.error) return String(parsed.error);
  } catch {
    if (body && body.length < 200 && !body.startsWith("<")) return body;
  }
  return fallback;
}

/** The parsed JSON body of an apiRequest error ("<status>: {json}"), when it has one. */
export function errorBody(err: unknown): Record<string, any> | null {
  const raw = String((err as any)?.message || "").replace(/^\d+:\s*/, "");
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

/** What POST .../publish-to-wordpress answers. Live: blogUrl / postId. Preview: dryRun + plan. */
export interface WpPublishResult {
  success?: boolean;
  ok?: boolean;
  dryRun?: boolean;
  /** "requested" = the caller asked for a preview; "role" = the caller may not publish live. */
  dryRunReason?: string;
  blocked?: boolean;
  alreadyPublished?: boolean;
  credentialsConfigured?: boolean;
  blogUrl?: string | null;
  publishedUrl?: string | null;
  postId?: number | string | null;
  wpPostId?: number | string | null;
  plan?: unknown;
  message?: string;
}

export function publishedUrlOf(d: WpPublishResult | null | undefined): string | null {
  return d?.blogUrl || d?.publishedUrl || null;
}

export function useStudioActions() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { toast } = useToast();

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });

  const approve = useMutation({
    mutationFn: async (id: number) => (await apiRequest("POST", `/api/content/${id}/approve`, {})).json().catch(() => ({})),
    onSuccess: () => {
      refresh();
      toast({ title: "Approved", description: "It now shows under Ready to publish." });
    },
    onError: (e) => toast({ title: "Could not approve", description: errorMessage(e, "Please try again."), variant: "destructive" }),
  });

  const reject = useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) =>
      (await apiRequest("POST", `/api/content/${id}/reject`, { reason, rejectionReason: reason })).json().catch(() => ({})),
    onSuccess: () => {
      refresh();
      toast({ title: "Rejected", description: "Draper's team will see your note." });
    },
    onError: (e) => toast({ title: "Could not reject", description: errorMessage(e, "Please try again."), variant: "destructive" }),
  });

  const schedule = useMutation({
    mutationFn: async ({ id, when }: { id: number; when: Date }) =>
      (await apiRequest("PATCH", `/api/content/${id}`, { status: "SCHEDULED", scheduledAt: when.toISOString() })).json().catch(() => ({})),
    onSuccess: (_d, vars) => {
      refresh();
      toast({ title: "Scheduled", description: vars.when.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) });
    },
    onError: (e) => toast({ title: "Could not schedule", description: errorMessage(e, "Please try again."), variant: "destructive" }),
  });

  /**
   * Publish an approved article. The server decides what happens:
   *  - owner/admin and `dryRun: false`: goes live ({blogUrl, postId}).
   *  - anyone else, or `dryRun: true`: nothing is posted and a `{dryRun: true, plan}` preview comes back.
   * The caller reads `data.dryRun` to tell the two apart, so a preview is never reported as "Published".
   */
  const publishWordPress = useMutation({
    mutationFn: async ({ id, dryRun }: { id: number; dryRun: boolean }): Promise<WpPublishResult> =>
      (await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/content/${id}/publish-to-wordpress`, { dryRun })).json().catch(() => ({})),
    onSuccess: (data) => {
      if (data?.dryRun === true) {
        toast({ title: "Preview only, nothing was published", description: "Here is what would be posted to WordPress." });
        return;
      }
      refresh();
      const url = publishedUrlOf(data);
      toast({
        title: data?.alreadyPublished ? "Already published" : "Published to WordPress",
        description: url ? `Live at ${url}` : "The article is live.",
      });
    },
    onError: (e) => {
      // A blocked publish comes back as an error that still carries the plan; the preview dialog explains it.
      if (errorBody(e)?.blocked) return;
      toast({ title: "WordPress publish failed", description: errorMessage(e, "Check the WordPress connection in Settings."), variant: "destructive" });
    },
  });

  return { approve, reject, schedule, publishWordPress };
}
